import _get from 'lodash/get'
import _size from 'lodash/size'
import _slice from 'lodash/slice'
import { each as eachPromise, all as allPromise } from 'bluebird'
import { Map, List, fromJS } from 'immutable'
import { get as apiGet, TYPE_BROOKLYN } from 'api-client'
import { setMultipleSeriesProcessing as setMultipleSeriesProcessingAction } from 'services/series/actions'
import { getSeriesMultiple } from 'services/series/middleware'
import { setMultipleVideosProcessing as setMultipleVideosProcessingAction } from 'services/videos/actions'
import { getVideoMultiple } from 'services/videos/middleware'
import { setMultiplePmPlacementsProcessing as setMultiplePmPlacementsProcessingAction } from 'services/pm-placement/actions'
import { getPmPlacementMultiple } from 'services/pm-placement/watchers-helpers'
import { setMultiplePmListsProcessing as setMultiplePmListsProcessingAction } from 'services/pm-list/actions'
import { getPmListMultiple } from 'services/pm-list/middleware'
import {
  setPmSection as setPmSectionAction,
  setPmSectionError as setPmSectionErrorAction,
  setPmSectionDone as setPmSectionDoneAction,
  setPmSectionProcessing as setPmSectionProcessingAction,
} from './actions'
import {
  getPmSectionProcessing,
  TYPE_CONTENT_LIST,
  TYPE_PEOPLE_LIST,
  TYPE_PLACEMENT,
  TYPE_PORTALS,
} from '.'

/**
 * Get multiple page manger sections and dispatch associated actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {String} options.language The request language
 * @param {String} options.screenType The page manager screen type
 * @param {Map} options.app The app state
 * @param {Map} options.auth The auth state
 * @param {Map} options.page The page state
 * @param {Map} options.pmList The page manager list state
 * @param {Map} options.pmPlacement The page manager placement state
 * @param {Map} options.pmSection The page manager section state
 * @param {Map} options.pmScreen The page manager screen
 * @param {Map} options.videos The videos state
 * @param {String[]|List|Set} options.sectionIds The page manager section ids
 * @param {Number} [options.limit] A limit for the number of page manager sections
 */
export async function getPmSectionMultiple (options = {}) {
  try {
    const {
      dispatch,
      language,
      screenType,
      app = Map(),
      auth = Map(),
      page = Map(),
      pmList = Map(),
      pmPlacement = Map(),
      pmSection = Map(),
      pmScreen = Map(),
      videos = Map(),
      limit,
    } = options
    let { sectionIds = [] } = options
    if (List.isList(sectionIds) || Set.isSet(sectionIds)) {
      sectionIds = sectionIds.toArray()
    }
    if (limit) {
      sectionIds = _slice(sectionIds, 0, limit)
    }
    await eachPromise(sectionIds, sectionId => getPmSectionSingle({
      dispatch,
      language,
      screenType,
      app,
      auth,
      page,
      pmList,
      pmPlacement,
      pmSection,
      pmScreen,
      videos,
      sectionId,
    }))
  } catch (e) {
    // Do nothing
  }
}

/**
 * Get one page manger section and dispatch associated actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {String} options.language The request language
 * @param {String} options.screenType The page manager screen type
 * @param {Map} options.app The app state
 * @param {Map} options.auth The auth state
 * @param {Map} options.page The page state
 * @param {Map} options.pmList The page manager list state
 * @param {Map} options.pmPlacement The page manager placement state
 * @param {Map} options.pmSection The page manager section state
 * @param {Map} options.pmScreen The page manager screen
 * @param {Map} options.videos The videos state
 * @param {String} options.sectionId The page manager section ids
 */
export async function getPmSectionSingle (options = {}) {
  const {
    dispatch,
    language,
    app = Map(),
    auth = Map(),
    page = Map(),
    pmList = Map(),
    pmPlacement = Map(),
    pmSection = Map(),
    videos = Map(),
    sectionId,
  } = options
  try {
    if (getPmSectionProcessing({ pmSection, sectionId })) {
      return
    }
    dispatch(setPmSectionProcessingAction(sectionId, true))
    const response = await apiGet(
      `page-manager/section/${sectionId}`, {
        language,
        // Remove disableMerchImpressionCount when web-app uses the section data for spotlight
        // and stops calling placement-content/:placementName from pm-placement
        disableMerchImpressionCount: false,
      },
      { auth },
      TYPE_BROOKLYN,
    )
    const data = fromJS(_get(response, 'body', {}))
    dispatch(setPmSectionAction({
      sectionId,
      data,
      processing: false,
      // Done will be set to true after all dependacies have loaded
      done: false,
    }))
    switch (data.get('type')) {
      case TYPE_PLACEMENT: {
        const placementName = data.getIn(['data', 'placement'])
        const placementNames = List([placementName])
        dispatch(setMultiplePmPlacementsProcessingAction(
          placementNames,
          language,
          true,
        ))
        await getPmPlacementMultiple({
          dispatch,
          placementNames,
          language,
          sectionId,
          auth,
          app,
          page,
          videos,
          pmPlacement,
        })
        break
      }
      case TYPE_CONTENT_LIST:
      case TYPE_PEOPLE_LIST:
      case TYPE_PORTALS: {
        const content = (data.getIn(['data', 'content']) || List())
        if (List.isList(content) && !content.isEmpty()) {
          // Set makes the values unique
          const listIds = content.toSet().map(v => v.get('listId')).toArray()
          dispatch(setMultiplePmListsProcessingAction(
            listIds,
            true,
          ))
          await getPmListMultiple({
            dispatch,
            language,
            sectionId,
            auth,
            listIds,
            pmList,
          })
        }
        break
      }
      default: {
        const tabs = (data.getIn(['data', 'tabs']) || List())
        const contentList = tabs.flatMap(v => v.get('tabContent'))
        const contentPromises = []
        // Set makes the values unique
        const videosList = contentList.filter(v => v.get('contentType') === 'video').toSet()
        if (!videosList.isEmpty()) {
          const ids = videosList.map(v => Number(v.get('contentId'))).toArray()
          dispatch(setMultipleVideosProcessingAction(
            ids,
            language,
            true,
          ))
          const promise = getVideoMultiple({
            dispatch,
            auth,
            videoIds: ids,
            language,
          })
          contentPromises.push(promise)
        }
        // Set makes the values unique
        const seriesList = contentList.filter(v => v.get('contentType') === 'series').toSet()
        if (!seriesList.isEmpty()) {
          const ids = seriesList.map(v => Number(v.get('contentId'))).toArray()
          dispatch(setMultipleSeriesProcessingAction(
            ids,
            language,
            true,
          ))
          const promise = getSeriesMultiple({
            dispatch,
            auth,
            seriesIds: ids,
            language,
          })
          contentPromises.push(promise)
        }
        if (_size(contentPromises) > 0) {
          await allPromise(contentPromises)
        }
        break
      }
    }
    // Set done to true now that all dependacies are loaded
    dispatch(setPmSectionDoneAction(sectionId, true))
  } catch (e) {
    dispatch(setPmSectionErrorAction(sectionId, e))
  }
}
